import React from "react";
import { alpha } from "@mui/material/styles";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";

// Whole numbers drop the cents ("AED 150", not "AED 150.00"); fractional
// prices keep them so nothing rounds away.
const formatSlotPrice = (price) => {
  const n = parseFloat(price || 0);
  return Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2);
};

function TimeSlotCard({
  time,
  price,
  currency = "AED",
  selected,
  onClick,
  disabled,
  trainerName,
  locationName,
  optionsLabel,
  pricePrefix,
  hidePrice = false,
  dense = false,
  align = "center",
  // A short tag above the subtitle ("Booked for you"): the time is already the client's.
  badge
}) {
  const subtitleColor = disabled ? "text.disabled" : selected ? "rgba(255,255,255,0.78)" : "text.secondary";
  // hidePrice: the parent grid found one uniform price across every rendered
  // slot (it already shows in the duration picker), so repeating it per cell is
  // pure noise. "Booked" still needs its line.
  const priceText = disabled ? "Booked" : hidePrice ? null : `${pricePrefix ? pricePrefix + " " : ""}${currency} ${formatSlotPrice(price)}`;
  const textAlign = align === "left" ? "left" : "center";
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: align === "left" ? "flex-start" : "center",
        py: dense ? 0.85 : 1.25,
        px: 1,
        borderRadius: 2,
        border: "1.5px solid",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected ? "primary.main" : "#fff",
        opacity: disabled ? 0.45 : 1,
        transition: "all 0.15s ease",
        boxShadow: (t) => (selected ? `0 4px 12px ${alpha(t.palette.primary.dark, 0.25)}` : "none"),
        "&:hover": !disabled && !selected ? { borderColor: "primary.main", boxShadow: "0 4px 12px rgba(26,26,26,0.08)" } : {}
      }}
    >
      <Typography
        sx={{
          fontSize: dense ? "0.95rem" : "1.05rem",
          fontWeight: 700,
          lineHeight: 1.2,
          letterSpacing: "-0.01em",
          color: disabled ? "text.disabled" : selected ? "#fff" : "text.primary"
        }}
      >
        {time}
      </Typography>
      {badge && !disabled && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.35,
            px: 0.6,
            py: 0.1,
            borderRadius: 1,
            fontWeight: 700,
            fontSize: dense ? "0.6rem" : "0.58rem",
            lineHeight: 1.4,
            letterSpacing: "0.02em",
            color: selected ? "primary.main" : "primary.dark",
            bgcolor: (t) => (selected ? "#fff" : alpha(t.palette.primary.main, 0.12))
          }}
        >
          {badge}
        </Typography>
      )}
      {priceText && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.25,
            fontWeight: 500,
            fontSize: dense ? "0.72rem" : "0.68rem",
            color: disabled ? "text.disabled" : selected ? "rgba(255,255,255,0.85)" : "text.secondary"
          }}
        >
          {priceText}
        </Typography>
      )}
      {optionsLabel && !disabled ? (
        <Typography
          variant="caption"
          sx={{
            mt: 0.25,
            fontWeight: 600,
            fontSize: dense ? "0.66rem" : "0.62rem",
            lineHeight: 1.3,
            textAlign,
            color: subtitleColor
          }}
        >
          {optionsLabel}
        </Typography>
      ) : (
        (trainerName || locationName) && !disabled && (
          <Typography
            variant="caption"
            sx={{
              mt: 0.25,
              fontWeight: 500,
              fontSize: dense ? "0.66rem" : "0.62rem",
              lineHeight: 1.3,
              textAlign,
              color: subtitleColor,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              wordBreak: "break-word"
            }}
          >
            {[trainerName, locationName].filter(Boolean).join(" · ")}
          </Typography>
        )
      )}
    </ButtonBase>
  );
}

export default TimeSlotCard;
